import { FC, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useFormikContext } from "formik";
import * as _ from "lodash";

import { DataDescriptorEntity, Enterprise, EnterpriseAppState, GamePlanFormValues } from "@ctra/api";
import { Nullable, Units } from "@ctra/utils";

import { useLocalization } from "@base";
import { useFarm } from "@farms";
import { useScorecard } from "@scorecard";

/**
 * Form Effects Wrapper
 * Provides bunch of useEffects to ensure form is in sync with global selections e.g. farm, unit system
 * works globally throughout the lifetime of GPT creation
 */
export const FormEffects: FC = ({ children }) => {
  const { values, setFieldValue } = useFormikContext<GamePlanFormValues>();
  const { unitSystem } = useLocalization();
  const { farm } = useFarm();
  const { value } = useScorecard();

  /**
   * get and set converted goal and new unit system if user switches the unit system
   */
  const { dataDescriptorID, hasTarget, goal, farmID, unitSystem: gamePlanUnitSystem } = values;

  /**
   * Get data descriptor (if available)
   */
  const dataDescriptor = useSelector<EnterpriseAppState, Nullable<DataDescriptorEntity>>((state) =>
    dataDescriptorID ? Enterprise.entities.getDataDescriptor(state, { id: dataDescriptorID }) : null
  );

  /**
   * maintain a unit system ref so we convert values only when it changes
   */
  const unitSystemRef = useRef<typeof unitSystem>(unitSystem);

  /**
   * If unit system changes, convert the goal value
   */
  useEffect(() => {
    if (dataDescriptor) {
      const {
        valueProperties: { units }
      } = dataDescriptor;

      if (unitSystemRef.current !== unitSystem && hasTarget) {
        setFieldValue(
          "goal",
          goal
            ? Units.convertToUnitSystem(goal, units, { from: unitSystemRef.current, to: unitSystem })
            : value
        );
        unitSystemRef.current = unitSystem;
      }
    }
  }, [goal, hasTarget, unitSystem, dataDescriptor, value, setFieldValue]);

  /**
   * Change unit system value in form if it changes
   */
  useEffect(() => {
    if (gamePlanUnitSystem !== unitSystem) {
      setFieldValue("unitSystem", unitSystem);
    }
  }, [unitSystem, setFieldValue, gamePlanUnitSystem]);

  /**
   * Change farm value in form if it changes from sidebar
   */
  useEffect(() => {
    if (farm && farmID !== farm.id) {
      setFieldValue("farmID", farm.id);
      setFieldValue("goal", null);
    }
  }, [farm, setFieldValue, hasTarget, farmID]);

  /**
   * When the farm or the data descriptor changes, set the new value of the goal to current value
   * For this to work, 'goal' is set to null upon a dataDescriptor change or a farm change
   */
  useEffect(() => {
    if (_.isNull(goal) && value && hasTarget) {
      setFieldValue("goal", value);
    }
  }, [value, setFieldValue, hasTarget, goal]);

  return <>{children}</>;
};
